import { useMemo } from 'react'
import moment from 'moment'
import { useQuery } from '@apollo/react-hooks'
import { GET_WORKING_HOUR_HISTORY } from 'modules/WorkingHours/graphql/Query'
import { GET_ALL_NSE_HOLIDAYS } from 'modules/NSEHoliday/graphql/Query'

// Same calculation as the server's utils/workingHours.js, so the countdown on
// screen matches the deadline the server stores. Keep the two in step.

const IST_OFFSET = 330
const MINUTES_IN_DAY = 24 * 60
const DAY_MS = 24 * 60 * 60 * 1000
const OFFSET_MS = IST_OFFSET * 60 * 1000

// Plain-number IST day helpers. These run for every day of every countdown
// each second, so they avoid moment (far too slow in that loop).
const istDayStart = (ms) =>
  Math.floor((ms + OFFSET_MS) / DAY_MS) * DAY_MS - OFFSET_MS
// 1 Jan 1970 was a Thursday (4)
const istDayOfWeek = (dayStart) =>
  (Math.floor((dayStart + OFFSET_MS) / DAY_MS) + 4) % 7
const istDateKey = (dayStart) =>
  new Date(dayStart + OFFSET_MS).toISOString().slice(0, 10)

export const DAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
]

const toMinutes = (time) => {
  const [h, m] = String(time || '').split(':').map(Number)
  if (Number.isNaN(h) || Number.isNaN(m)) return null
  return h * 60 + m
}

const mergeIntervals = (intervals) => {
  const sorted = intervals
    .filter(([s, e]) => s !== null && e !== null && e > s)
    .sort((a, b) => a[0] - b[0])
  return sorted.reduce((acc, [s, e]) => {
    const prev = acc[acc.length - 1]
    if (prev && s <= prev[1]) prev[1] = Math.max(prev[1], e)
    else acc.push([s, e])
    return acc
  }, [])
}

// null when the week has no working time at all (that period then falls back
// to the 24x7 clock, as on the server)
const weekToDays = (week = []) => {
  const days = {}
  week.forEach((row) => {
    if (row.isClosed) days[row.dayOfWeek] = []
    else if (row.isOpen24Hours) days[row.dayOfWeek] = [[0, MINUTES_IN_DAY]]
    else
      days[row.dayOfWeek] = mergeIntervals(
        (row.slots || []).map((slot) => [
          toMinutes(slot.openTime),
          toMinutes(slot.closeTime),
        ])
      )
  })
  return Object.values(days).some((d) => d.length) ? days : null
}

// history: [{ effectiveFrom, week }]. Each week applies from its effectiveFrom
// until the next entry, so past days keep the hours that applied then.
export const buildCalendar = (history = [], holidays = []) => {
  const sorted = [...history].sort(
    (a, b) => new Date(a.effectiveFrom) - new Date(b.effectiveFrom)
  )
  const versions = sorted.map((entry, i) => ({
    from: new Date(entry.effectiveFrom).getTime(),
    to: sorted[i + 1]
      ? new Date(sorted[i + 1].effectiveFrom).getTime()
      : Infinity,
    days: weekToDays(entry.week),
  }))
  if (versions.length) versions[0].from = -Infinity

  return {
    versions,
    holidays: new Set(
      holidays.map((h) => moment(h.date, 'YYYY-MM-DD').format('YYYY-MM-DD'))
    ),
    alwaysOpen: !versions.length,
  }
}

const intervalsForDay = (calendar, dayFrom) => {
  const dayTo = dayFrom + DAY_MS
  const isHoliday = calendar.holidays.has(istDateKey(dayFrom))
  const dayOfWeek = istDayOfWeek(dayFrom)
  const result = []

  calendar.versions.forEach((version) => {
    if (version.from >= dayTo || version.to <= dayFrom) return
    let ranges
    if (!version.days) ranges = [[0, MINUTES_IN_DAY]]
    else if (isHoliday) return
    else ranges = version.days[dayOfWeek] || []

    ranges.forEach(([s, e]) => {
      const open = Math.max(dayFrom + s * 60000, version.from)
      const close = Math.min(dayFrom + e * 60000, version.to)
      if (close > open) result.push([open, close])
    })
  })
  return result.sort((a, b) => a[0] - b[0])
}

// Working time in ms between two instants (negative when `to` is before `from`)
export const workingMsBetween = (calendar, from, to) => {
  const fromMs = moment(from).valueOf()
  const toMs = moment(to).valueOf()
  if (toMs < fromMs) return -workingMsBetween(calendar, to, from)
  if (!calendar || calendar.alwaysOpen) return toMs - fromMs

  let dayStart = istDayStart(fromMs)
  let total = 0

  while (dayStart < toMs) {
    for (const [open, close] of intervalsForDay(calendar, dayStart)) {
      const overlap = Math.min(close, toMs) - Math.max(open, fromMs)
      if (overlap > 0) total += overlap
    }
    dayStart += DAY_MS
  }
  return total
}

export const isWorkingTime = (calendar, at = moment()) => {
  if (!calendar || calendar.alwaysOpen) return true
  const atMs = moment(at).valueOf()
  return intervalsForDay(calendar, istDayStart(atMs)).some(
    ([open, close]) => atMs >= open && atMs < close
  )
}

// SLA countdown label. The value is working time, so it is shown as total
// hours (e.g. -279:00:00) rather than days, which would read as calendar days.
export const formatSlaTime = (ms) => {
  const abs = Math.abs(ms)
  const hours = Math.floor(abs / 3600000)
  const minutes = Math.floor((abs % 3600000) / 60000)
  const seconds = Math.floor((abs % 60000) / 1000)
  const pad = (n) => String(n).padStart(2, '0')
  return `${ms < 0 ? '-' : ''}${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
}

// Until both lists load, the calendar is null and the countdown falls back to
// plain clock time
export const useWorkingCalendar = () => {
  const { data: historyData } = useQuery(GET_WORKING_HOUR_HISTORY, {
    fetchPolicy: 'cache-and-network',
  })
  const { data: holidayData } = useQuery(GET_ALL_NSE_HOLIDAYS, {
    fetchPolicy: 'cache-and-network',
  })

  return useMemo(() => {
    if (!historyData?.getWorkingHourHistory || !holidayData?.getHoliday)
      return null
    return buildCalendar(
      historyData.getWorkingHourHistory,
      holidayData.getHoliday
    )
  }, [historyData, holidayData])
}
